import React, { useId } from 'react'
import {
  ContentfulCardStyle,
  ContentfulPageSection as ContentfulPageSectionType,
} from '@reward-platform/contentful'
import { BadgeProps, Eyebrow, Heading, Section } from '@alto-avios/alto-ui'
import { EyebrowProps } from '@alto-avios/alto-ui/dist/components/Eyebrow/Eyebrow'
import { SearchPageSection } from '../search'
import { RichText } from './ContentfulRichText'
import ContentfulCta from './ContentfulCta'
import { ContentfulCard } from './ContentfulCard'
import { ContentfulCardCarousel } from './ContentfulCardCarousel'

const CARD_LIMITS = {
  inline: 2,
  carousel: 10,
  default: 5,
} as const

const getCardLimit = (cardsVariant: ContentfulPageSectionType['cardsVariant']) => {
  if (cardsVariant === 'inline') {
    return CARD_LIMITS.inline
  }

  if (cardsVariant === 'carousel') {
    return CARD_LIMITS.carousel
  }

  return CARD_LIMITS.default
}

type BadgeVariant = NonNullable<BadgeProps['styleVariant']>
type EyebrowVariant = NonNullable<EyebrowProps['styleVariant']>

const BADGE_TO_EYEBROW: Record<BadgeVariant, EyebrowVariant> = {
  info: 'activate',
  warning: 'inspiration',
  success: 'collect',
  brand: 'spend',
  caution: 'inspiration',
  critical: 'inspiration',
  white: 'spend',
  neutral: 'spend',
  sale: 'inspiration',
}

function mapBadgeToEyebrow(badge: BadgeVariant): EyebrowVariant {
  if (badge) {
    return BADGE_TO_EYEBROW[badge]
  }
  return 'spend'
}

const resolveCardStyle = (
  cardStyle: ContentfulPageSectionType['cardStyle'],
  tallCardVariant: boolean
): ContentfulCardStyle => cardStyle ?? (tallCardVariant ? 'tall' : 'short')

const ContentfulPageSection = ({
  identifier,
  heading,
  contentAlignment,
  description,
  cards,
  cta,
  badge,
  cardsVariant,
  cardStyle,
  tallCardVariant = true,
  entryId,
  revisionId,
}: ContentfulPageSectionType) => {
  const headingId = useId()

  const cardLimit = getCardLimit(cardsVariant)
  const displayCards = cards?.slice(0, cardLimit) ?? []
  const isCarousel = cardsVariant === 'carousel'
  const resolvedCardStyle = resolveCardStyle(cardStyle, tallCardVariant)
  const sectionName = heading || badge?.label || identifier || 'Page section'
  const eventData = {
    sectionId: identifier ?? 'page section',
    sectionHeading: sectionName,
    contentfulEntryId: entryId,
    contentfulRevisionId: revisionId,
  }
  const getHeadingSize = () => {
    if (!isCarousel) {
      return 'lg'
    }
    return resolvedCardStyle === 'short' ? 'xs' : 'md'
  }
  return (
    <section aria-label={sectionName} aria-labelledby={heading ? headingId : undefined}>
      <Section
        containerMaxWidth="page"
        paddingX="default"
        paddingY="default"
        flexDirection="row"
        justifyContent="center"
        alignItems="start"
      >
        <Section.Container>
          <SearchPageSection inline={cardsVariant === 'inline'}>
            <SearchPageSection.ContentContainer alignment={contentAlignment}>
              {badge?.label ? (
                <Eyebrow styleVariant={mapBadgeToEyebrow(badge.variant as BadgeVariant)}>
                  {badge.label}
                </Eyebrow>
              ) : null}
              <Heading as="h2" size={getHeadingSize()} id={headingId}>
                {heading}
              </Heading>
              {description ? <RichText document={description} sizes={{ p: 'lg' }} /> : null}
              {cta ? (
                <ContentfulCta
                  {...cta}
                  ariaLabel={`${cta.label}: ${sectionName}`}
                  eventData={eventData}
                />
              ) : null}
            </SearchPageSection.ContentContainer>
            {isCarousel ? (
              <ContentfulCardCarousel
                cards={displayCards}
                eventData={eventData}
                sectionName={sectionName}
                cardStyle={resolvedCardStyle}
              />
            ) : (
              <SearchPageSection.CardContainer>
                {displayCards.map((card, index) =>
                  card ? (
                    <ContentfulCard
                      key={`${card.identifier}-card-${String(index)}`}
                      {...card}
                      eventData={eventData}
                      cardStyle={resolvedCardStyle}
                    />
                  ) : null
                )}
              </SearchPageSection.CardContainer>
            )}
          </SearchPageSection>
        </Section.Container>
      </Section>
    </section>
  )
}

export default ContentfulPageSection
